import { gql } from '@apollo/client'
import {
	RECRUITER_FRAGMENT,
	JOB_FRAGMENT,
	WORKER_FRAGMENT,
	WORKED_SHIFT_FRAGMENT,
	WORKED_EXPENSE_FRAGMENT,
	WORKED_TIMESHEET_FRAGMENT,
	ACTIVE_JOB_FRAGMENT,
	RECRUITER_WORKER_ONBOARDING_PART_INTEGRATION_FRAGMENT,
} from '~/common/apollo/apollo.fragment'

export const SALESFORCE_OAUTH_LOGIN_QUERY = gql`
	query salesforceOauthLogin($companyId: String!, $codeChallenge: String!, $state: String!) {
		salesforceOauthLogin(companyId: $companyId, codeChallenge: $codeChallenge, state: $state) {
			enabled
			authorizeUrl
		}
	}
`

export const VERIFY_RECRUITER_QUERY = gql`
	query verifyRecruiter($token: String!) {
		recruiterToken(token: $token) {
			token
			recruiter {
				...recruiterFragment
			}
		}
	}

	${RECRUITER_FRAGMENT}
`

export const GET_RECRUITER_WORKER_ACTIVE_JOBS_QUERY = gql`
	query getRecruiterWorkerActiveJobsQuery($workerId: String!) {
		recruiterWorkerActiveJobs(workerId: $workerId) {
			completedJobs {
				...jobFragment
			}
			currentJobs {
				...jobFragment
			}
			nextJobs {
				...jobFragment
			}
		}
	}

	${ACTIVE_JOB_FRAGMENT}
`

export const GET_RECRUITER_WOKER_FAVORITED_JOBS_QUERY = gql`
	query getRecruiterWorkerFavoritedJobsQuery($workerId: String!, $after: String, $before: String, $first: Int, $last: Int) {
		recruiterWorkerFavoritedJobs(workerId: $workerId, after: $after, before: $before, first: $first, last: $last) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				...jobFragment
			}
		}
	}

	${JOB_FRAGMENT}
`

export const GET_RECRUITER_WORKER_SUGGESTION_JOBS_QUERY = gql`
	query getRecruiterWorkerSuggestionJobsQuery($workerId: String!, $after: String, $before: String, $first: Int, $last: Int) {
		recruiterWorkerSuggestionJobs(workerId: $workerId, after: $after, before: $before, first: $first, last: $last) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				...jobFragment
			}
		}
	}

	${JOB_FRAGMENT}
`

export const GET_RECRUITER_WORKER_CURRENT_WORKED_TIMESHEET_QUERY = gql`
	query getCurrentRecruiterWorkerWorkedTimesheet($workerId: String!) {
		recruiterWorkerCurrentWorkedTimesheet(workerId: $workerId) {
			...workedTimesheetFragment

			job {
				...jobFragment
			}

			timesheetImages {
				id
				filename
				fileUrl
			}

			workedShifts {
				...workedShiftFragment
			}

			workerExpenses {
				...workedExpenseFragment
			}
		}
	}

	${JOB_FRAGMENT}
	${WORKED_SHIFT_FRAGMENT}
	${WORKED_EXPENSE_FRAGMENT}
	${WORKED_TIMESHEET_FRAGMENT}
`

export const GET_RECRUITER_WORKER_PREVIOUS_WORKED_TIMESHEET_QUERY = gql`
	query getRecruiterWorkerPreviousWorkedTimesheet($workerId: String!) {
		recruiterWorkerPreviousWorkedTimesheet(workerId: $workerId) {
			...workedTimesheetFragment
			workedShiftsOverview

			job {
				...jobFragment
			}

			timesheetImages {
				id
				filename
				fileUrl
			}

			workedShifts {
				...workedShiftFragment
			}

			workerExpenses {
				...workedExpenseFragment
			}
		}
	}

	${JOB_FRAGMENT}
	${WORKED_SHIFT_FRAGMENT}
	${WORKED_EXPENSE_FRAGMENT}
	${WORKED_TIMESHEET_FRAGMENT}
`

export const GET_RECRUITER_WOKER_ASSIGNMENTS_QUERY = gql`
	query getRecruiterWorkerAssignmentsQuery($workerId: String, $jobId: String, $after: String, $before: String, $first: Int, $last: Int) {
		recruiterWorkerAssignments(workerId: $workerId, jobId: $jobId, after: $after, before: $before, first: $first, last: $last) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				id
				status
				createdAt
				updatedAt
				startDate
				endDate
				isCurrentAssignment
				externalUrl
				job {
					...jobFragment
				}
				worker {
					...workerFragment
				}
			}
		}
	}

	${JOB_FRAGMENT}
	${WORKER_FRAGMENT}
`

export const GET_RECRUITER_WORKER_ONBOARDING_OVERVIEW_QUERY = gql`
	query getRecruiterWorkerOnboardingQuery($workerId: String!) {
		recruiterWorkerOnboardingOverview(workerId: $workerId) {
			id
			percentageCompleted
			state
			onboardingParts {
				name
				workerOnboardingPart {
					id
					integrationConfigurations
					integrationObject
					state
					integration {
						...workerOnboardingPartIntegrationFragment
					}
				}
			}
		}
	}
	${RECRUITER_WORKER_ONBOARDING_PART_INTEGRATION_FRAGMENT}
`

export const GET_RECRUITER_WORKER_QUERY = gql`
	query getRecruiterWorkerQuery($workerId: String!) {
		recruiterWorker(workerId: $workerId) {
			id
			lastSignInAt
			lastSignInOn
			translatedStatus
			preferredShifts
			nextPlacementStartDate
			preferredJobType
			referralMethod
			completedJobsNumber
			workerSkillChecklistsCount
			workerAddresses {
				id
				street
				city
				state
				zipcode
				default
			}
			recruiter {
				id
				name
				email
			}
			workExperienceOverview {
				discipline
				primarySpecialty
				secondarySpecialty
				yearsOfExperience
			}
			workingPreferredLocations {
				id
				preferredWorkingState
			}
			workerCertifications {
				id
				title
				licenseState
				licenseDiscipline
				certificationType
				compactLicense
				expirationDate
			}
			...workerFragment
		}
	}

	${WORKER_FRAGMENT}
`

export const GET_RECRUITER_CANDIDATE_ACTIVITIES_QUERY = gql`
	query getRecruiterCandidateActivitiesQuery($workerId: String!, $activityTypes: [CandidateActivityTypeEnum!], $after: String, $first: Int) {
		recruiterCandidateActivities(workerId: $workerId, activityTypes: $activityTypes, after: $after, first: $first) {
			totalCount
			pageInfo {
				hasNextPage
				endCursor
			}
			nodes {
				id
				kind
				label
				message
				eventTime
				relatedJob {
					id
					title
					externalJobId
				}
			}
		}
	}
`

export const GET_RECRUITER_DETAIL_RANKED_JOBS_QUERY = gql`
	query getRecruiterWorkerRankedJobsQuery($workerId: String!, $state: String!, $city: String!, $after: String, $before: String, $first: Int, $last: Int) {
		recruiterDetailPopularCity(workerId: $workerId, state: $state, city: $city, after: $after, before: $before, first: $first, last: $last) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				city
				state
				avgWeeklyRate
				totalJobs
				rankedJobs {
					matchingPercentage
					job {
						...jobFragment
					}
				}
			}
		}
	}

	${JOB_FRAGMENT}
`
